import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Navigation, RefreshCw, Phone, Clock, Truck, CheckCircle, WifiOff, Users, ChevronDown, ChevronUp } from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Module-level singleton so StrictMode / remounts / parallel components
// never append the Maps JS loader more than once.
let _mapsLoaderPromise = null;
const loadGoogleMapsOnce = () => {
  if (_mapsLoaderPromise) return _mapsLoaderPromise;
  if (typeof window !== "undefined" && window.google?.maps) {
    _mapsLoaderPromise = Promise.resolve(window.google);
    return _mapsLoaderPromise;
  }
  _mapsLoaderPromise = new Promise((resolve) => {
    // Reuse existing tag if any other component already appended it
    const existing = document.querySelector('script[data-doozygo-maps-loader="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      if (window.google?.maps) resolve(window.google);
      return;
    }
    const cbName = "__doozygoMapsReady";
    window[cbName] = () => resolve(window.google);
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&callback=${cbName}`;
    script.async = true;
    script.defer = true;
    script.dataset.doozygoMapsLoader = "1";
    document.head.appendChild(script);
  });
  return _mapsLoaderPromise;
};

const STATUS_CONFIG = {
  en_route: { label: "En Route", color: "#3b82f6", icon: "🚗", bgColor: "bg-blue-100 text-blue-700" },
  on_site: { label: "On Site", color: "#10b981", icon: "📍", bgColor: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Completed", color: "#8b5cf6", icon: "✅", bgColor: "bg-purple-100 text-purple-700" },
  offline: { label: "Offline", color: "#94a3b8", icon: "⚫", bgColor: "bg-gray-100 text-gray-500" },
};

const LiveTrackerMap = ({ auth, authAxios }) => {
  const [decorators, setDecorators] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDecorator, setSelectedDecorator] = useState(null);
  const [showList, setShowList] = useState(true);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});
  const infoWindowRef = useRef(null);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API_URL}/api/tracker/active`);
      setDecorators(res.data.active || []);
      setTeam(res.data.team || []);
    } catch (err) { logger.error("[Tracker] Fetch error:", err.message); }
    setLoading(false);
  }, [authAxios]);

  // Load Google Maps script (singleton — safe across remounts + StrictMode)
  useEffect(() => {
    let cancelled = false;
    loadGoogleMapsOnce().then(() => {
      if (cancelled) return;
      // no-op: the map init effect polls window.google.maps below
    });
    return () => { cancelled = true; };
  }, []);

  // Initialize map
  useEffect(() => {
    const init = () => {
      if (!mapRef.current || !window.google?.maps) return;
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 19.076, lng: 72.8777 }, // Mumbai default
        zoom: 12,
        styles: [
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "simplified" }] },
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });
      infoWindowRef.current = new window.google.maps.InfoWindow();
    };
    const timer = setInterval(() => {
      if (window.google?.maps) { init(); clearInterval(timer); }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  // Update markers when decorators change
  useEffect(() => {
    if (!mapInstance.current || !window.google?.maps) return;

    // Remove old markers not in active list
    const activePhones = new Set(decorators.map(d => d.phone));
    Object.keys(markersRef.current).forEach(phone => {
      if (!activePhones.has(phone)) {
        markersRef.current[phone].setMap(null);
        delete markersRef.current[phone];
      }
    });

    // Add/update markers
    const bounds = new window.google.maps.LatLngBounds();
    decorators.forEach(d => {
      const pos = { lat: d.lat, lng: d.lng };
      const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.offline;
      bounds.extend(pos);

      if (markersRef.current[d.phone]) {
        // Update existing marker position
        markersRef.current[d.phone].setPosition(pos);
      } else {
        // Create new marker
        const marker = new window.google.maps.Marker({
          position: pos,
          map: mapInstance.current,
          title: d.name,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: cfg.color,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 3,
          },
          label: { text: d.name?.[0] || "?", color: "#fff", fontSize: "11px", fontWeight: "bold" },
        });

        marker.addListener("click", () => {
          const booking = d.assigned_booking;
          const content = `
            <div style="font-family:sans-serif;min-width:200px;padding:4px">
              <div style="font-weight:bold;font-size:14px;color:#1E1B4B;margin-bottom:4px">${d.name || "Unknown"}</div>
              <div style="font-size:12px;color:#475569;margin-bottom:2px">📞 ${d.phone}</div>
              <div style="font-size:12px;margin-bottom:6px">
                <span style="background:${cfg.color};color:white;padding:2px 8px;border-radius:10px;font-size:11px">${cfg.label}</span>
              </div>
              <div style="font-size:11px;color:#94a3b8">Last update: ${d.last_updated ? new Date(d.last_updated).toLocaleTimeString() : "—"}</div>
              ${d.accuracy ? `<div style="font-size:11px;color:#94a3b8">Accuracy: ~${Math.round(d.accuracy)}m</div>` : ""}
              ${booking ? `
                <div style="margin-top:8px;padding-top:8px;border-top:1px solid #eee">
                  <div style="font-size:11px;font-weight:bold;color:#E83E8C">Today's Booking</div>
                  <div style="font-size:11px;color:#475569">${booking.customer_name || ""} — ${booking.package || ""}</div>
                  <div style="font-size:11px;color:#475569">📍 ${booking.address || ""}</div>
                  <div style="font-size:11px;color:#475569">🕐 ${booking.setup_time || ""}</div>
                </div>
              ` : ""}
            </div>
          `;
          infoWindowRef.current.setContent(content);
          infoWindowRef.current.open(mapInstance.current, marker);
          setSelectedDecorator(d);
        });

        markersRef.current[d.phone] = marker;
      }
    });

    // Fit map to markers
    if (decorators.length > 0) {
      mapInstance.current.fitBounds(bounds);
      if (decorators.length === 1) mapInstance.current.setZoom(15);
    }
  }, [decorators]);

  // Poll every 15 seconds
  useEffect(() => {
    fetchLocations();
    const interval = setInterval(fetchLocations, 15000);
    return () => clearInterval(interval);
  }, [fetchLocations]);

  const timeAgo = (isoStr) => {
    if (!isoStr) return "—";
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <div className="space-y-4" data-testid="live-tracker">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-[#1E1B4B] flex items-center gap-2">
          <Navigation size={20} className="text-[#E83E8C]" />
          Live Field Tracker
          <span className="text-sm font-normal text-[#94a3b8]">({decorators.length} active)</span>
        </h2>
        <button onClick={() => { setLoading(true); fetchLocations(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E83E8C]/10 text-[#E83E8C] text-sm font-medium hover:bg-[#E83E8C]/20" data-testid="tracker-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Map */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div ref={mapRef} className="w-full h-[450px] bg-gray-100" data-testid="tracker-map">
          {!MAPS_KEY && (
            <div className="flex items-center justify-center h-full text-[#94a3b8] text-sm">
              Google Maps API key not configured
            </div>
          )}
        </div>
      </div>

      {/* Team List */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <button
          onClick={() => setShowList(!showList)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
          data-testid="tracker-list-toggle"
        >
          <span className="font-bold text-[#1E1B4B] flex items-center gap-2">
            <Users size={16} className="text-[#E83E8C]" /> Team Status
          </span>
          {showList ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showList && (
          <div className="border-t border-gray-100">
            {decorators.length === 0 ? (
              <div className="p-6 text-center text-[#94a3b8] text-sm">
                <WifiOff size={24} className="mx-auto mb-2 text-gray-300" />
                No decorators are currently sharing their location.
                <br />They need to open <span className="font-mono text-[#E83E8C]">/field-tracker</span> on their phone.
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {decorators.map(d => {
                  const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.offline;
                  const booking = d.assigned_booking;
                  return (
                    <div
                      key={d.phone}
                      className={`p-4 hover:bg-[#FFFAF5] cursor-pointer transition-colors ${selectedDecorator?.phone === d.phone ? "bg-[#E83E8C]/5" : ""}`}
                      onClick={() => {
                        setSelectedDecorator(d);
                        if (mapInstance.current && d.lat && d.lng) {
                          mapInstance.current.panTo({ lat: d.lat, lng: d.lng });
                          mapInstance.current.setZoom(16);
                        }
                      }}
                      data-testid={`tracker-decorator-${d.phone}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center text-white font-bold text-sm shrink-0">
                          {(d.name || "?")[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm text-[#1E1B4B]">{d.name || "Unknown"}</p>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.bgColor}`}>{cfg.label}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-[#94a3b8] flex items-center gap-1"><Phone size={10} /> {d.phone}</span>
                            <span className="text-xs text-[#94a3b8] flex items-center gap-1"><Clock size={10} /> {timeAgo(d.last_updated)}</span>
                            {d.accuracy && <span className="text-xs text-[#94a3b8]">~{Math.round(d.accuracy)}m</span>}
                          </div>
                        </div>
                      </div>
                      {booking && (
                        <div className="mt-2 ml-13 pl-13 text-xs bg-[#E83E8C]/5 rounded-lg p-2 ml-[52px]">
                          <span className="text-[#E83E8C] font-medium">Assigned:</span>{" "}
                          <span className="text-[#475569]">{booking.customer_name} — {booking.package} — {booking.setup_time}</span>
                          <br />
                          <span className="text-[#94a3b8] flex items-center gap-1 mt-0.5"><MapPin size={10} /> {booking.address}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveTrackerMap;
